import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'

interface RejectParams {
  userStoryId: string
  reason: string
}

interface RpcResult {
  success: boolean
  error?: string
  message_id?: string
  rejection_reason?: string
}

/**
 * Calls the Supabase RPC function to reject a user story.
 * Stores the rejection reason and posts a message in the exchange zone.
 * The US stays in 'pending_validation' status for revision.
 */
async function rejectUserStory({ userStoryId, reason }: RejectParams): Promise<void> {
  const { data, error } = await supabase.rpc('reject_user_story', {
    p_user_story_id: userStoryId,
    p_reason: reason,
  })

  if (error) {
    throw new Error(error.message)
  }

  const result = data as RpcResult
  if (!result.success) {
    throw new Error(result.error || 'Failed to reject user story')
  }
}

export function useRejectUserStory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: rejectUserStory,
    onSuccess: () => {
      // Invalidate user story queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['userStory'] })
      // Also invalidate exchange messages for this US
      queryClient.invalidateQueries({ queryKey: ['exchangeMessages'] })
    },
  })
}
